import { bloqueioFoiAutomatico } from '../../api/dispositivos.js';
import type { Dispositivo } from '../../api/dispositivos.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { formatarDataHora } from '../../componentes/datas.js';
import './DialogosDeBloqueio.css';

type Props = {
  readonly dispositivo: Dispositivo;
  readonly agindo: boolean;
  readonly erro: string | null;
  readonly aoConfirmar: () => void;
  readonly aoCancelar: () => void;
};

/**
 * Bloqueio integral do aparelho (UC12 2a, RF-004).
 *
 * O bloqueio vale para TODA rota `/app/*` — o aparelho para de falar com o
 * middleware, inclusive para subir o que já registrou. Por isso a confirmação
 * é explícita e diz, em número, o que está em risco: quem clica precisa saber
 * que pode estar jogando fora contagem de armazém.
 */
export function DialogoDeBloqueio({ dispositivo, agindo, erro, aoConfirmar, aoCancelar }: Props) {
  const pendentes = dispositivo.operacoesPendentes;

  return (
    <DialogoConfirmacao
      titulo={`Bloquear o aparelho ${dispositivo.identificador}?`}
      rotuloConfirmar="Bloquear aparelho"
      agindo={agindo}
      aoConfirmar={aoConfirmar}
      aoCancelar={aoCancelar}
    >
      <p>
        O aparelho para de falar com o middleware em qualquer rota, inclusive nas de enviar o que já
        foi registrado.
      </p>
      {pendentes > 0 ? (
        <p className="dialogo-bloqueio__risco">
          As {pendentes} operações que este aparelho ainda não sincronizou podem se perder.
        </p>
      ) : (
        <p>
          Ele não declarou nenhuma operação pendente na última sincronização
          {dispositivo.ultimaSync === null
            ? '.'
            : ` (${formatarDataHora(dispositivo.ultimaSync)}).`}{' '}
          Mesmo assim, o que foi registrado depois disso pode se perder.
        </p>
      )}
      {erro !== null && <p className="dialogo-bloqueio__erro">{erro}</p>}
    </DialogoConfirmacao>
  );
}

/**
 * Desbloqueio.
 *
 * Devolver um aparelho à operação é decisão de gente, e a decisão muda de
 * natureza conforme quem o tirou do ar: desfazer o bloqueio de um colega é uma
 * coisa; soltar o que o freio de autenticação travou sozinho é outra — ali
 * houve um incidente, e ninguém o investigou ainda.
 */
export function DialogoDeDesbloqueio({
  dispositivo,
  agindo,
  erro,
  aoConfirmar,
  aoCancelar,
}: Props) {
  const automatico = bloqueioFoiAutomatico(dispositivo);

  return (
    <DialogoConfirmacao
      titulo={`Desbloquear o aparelho ${dispositivo.identificador}?`}
      rotuloConfirmar="Desbloquear aparelho"
      variante="primario"
      agindo={agindo}
      aoConfirmar={aoConfirmar}
      aoCancelar={aoCancelar}
    >
      {automatico ? (
        <p className="dialogo-bloqueio__risco">
          Este aparelho foi travado pelo freio de autenticação, e não por decisão de alguém. Houve
          tentativas de entrada malsucedidas: confirme com quem opera antes de devolvê-lo à
          operação.
        </p>
      ) : (
        <p>
          Bloqueado por {dispositivo.bloqueadoPor ?? 'alguém do painel'}
          {dispositivo.bloqueadoEm === null
            ? ''
            : ` em ${formatarDataHora(dispositivo.bloqueadoEm)}`}
          .
        </p>
      )}
      <p>Ao desbloquear, ele volta a falar com o middleware e a enviar o que tiver na fila.</p>
      {erro !== null && <p className="dialogo-bloqueio__erro">{erro}</p>}
    </DialogoConfirmacao>
  );
}
